/* ClientReportDialog — pick a date range, preview a branded per-client activity
 * report, and save it as a PDF (reuses exportNodeToPdf). Default range: last 7 days. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, FileDown, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { ClientReportDocument } from './ClientReportDocument';
import { exportNodeToPdf } from '../lib/proposalPdf';

const iso = (d) => d.toISOString();
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); d.setHours(0, 0, 0, 0); return d; };
const toInputDate = (d) => d.toISOString().slice(0, 10);

export const ClientReportDialog = ({ client, onClose }) => {
  const { api, user } = useAuth();
  const open = !!client;
  const [start, setStart] = useState(toInputDate(daysAgo(7)));
  const [end, setEnd] = useState(toInputDate(new Date()));
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [report, setReport] = useState(null);
  const docRef = useRef(null);

  const company = user?.business_name || user?.name || 'PostToSocial';

  const load = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    try {
      const startIso = iso(new Date(`${start}T00:00:00`));
      const endIso = iso(new Date(`${end}T23:59:59`));
      const [an, posts] = await Promise.all([
        api.get('/analytics', { params: { client_id: client.id, start: startIso, end: endIso } }),
        api.get('/posts', { params: { client_id: client.id, status: 'published', limit: 10 } }),
      ]);
      const d = an.data;
      const recent = (posts.data?.posts || posts.data || []).slice(0, 10).map((p) => ({
        id: p.id,
        content: p.content || p.caption || '',
        platform: p.platform,
        date: p.published_at || p.scheduled_time || p.created_at,
      }));
      setReport({
        window: d.window,
        summary: d.summary,
        byPlatform: d.by_platform || [],
        recentPosts: recent,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not build report');
    } finally { setLoading(false); }
  }, [api, client, start, end]);

  useEffect(() => { if (client) load(); }, [client, load]);

  const download = async () => {
    setDownloading(true);
    try {
      await exportNodeToPdf(docRef.current, `${(client.name || 'client').replace(/\s+/g, '-')}-report.pdf`);
      toast.success('Report saved');
    } catch { toast.error('Could not generate PDF'); }
    finally { setDownloading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col p-0" data-testid="client-report-dialog">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-indigo-600" /> Client report — {client?.name}</DialogTitle>
          <DialogDescription>A branded activity summary for the selected date range.</DialogDescription>
        </DialogHeader>

        <div className="px-6 flex items-end gap-3 flex-wrap">
          <div><Label className="text-xs">From</Label><Input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} data-testid="report-start-input" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} data-testid="report-end-input" /></div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="report-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update'}
          </Button>
        </div>

        <div className="overflow-y-auto px-6 mt-3 flex-1 bg-zinc-100">
          <div className="my-4 shadow-lg rounded-lg overflow-hidden bg-white">
            {loading || !report ? (
              <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
            ) : (
              <ClientReportDocument
                ref={docRef}
                company={company}
                client={client}
                window={report.window}
                summary={report.summary}
                byPlatform={report.byPlatform}
                recentPosts={report.recentPosts}
              />
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-zinc-100">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button onClick={download} disabled={downloading || loading || !report} data-testid="report-download-btn">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />} Save to desktop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClientReportDialog;
